import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Lock, Loader2, ShoppingCart, ArrowRight, ExternalLink, ArrowLeft } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

const money = (c) => `$${(c / 100).toFixed(0)}`;

export default function Billing() {
  const [labs, setLabs] = useState([]);
  const [legacy, setLegacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState("monthly");
  const [cart, setCart] = useState([]); // slugs
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = () => api.get("/catalog").then(({ data }) => {
    setLabs(data.labs); setLegacy(data.legacy_access);
    const pre = params.get("lab");
    if (pre && data.labs.some((l) => l.slug === pre && !l.owned)) setCart([pre]);
  }).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const owned = labs.filter((l) => l.owned);
  const available = labs.filter((l) => !l.owned);
  const toggle = (slug) => setCart((c) => c.includes(slug) ? c.filter((x) => x !== slug) : [...c, slug]);

  useEffect(() => {
    if (cart.length === 0) { setReview(null); return; }
    api.post("/billing/review", { products: cart, interval })
      .then(({ data }) => setReview(data))
      .catch((e) => { setReview(null); toast.error(apiErr(e)); });
  }, [cart, interval]);

  const checkout = async () => {
    setBusy(true); setManual(null);
    try {
      const { data } = await api.post("/billing/checkout", { products: cart, interval, origin_url: window.location.origin });
      if (data.url && data.mode !== "manual") { window.location.assign(data.url); return; }
      setManual(data); // manual fallback only when Stripe isn't configured
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="billing-page">
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm" data-testid="billing-home"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
          <h1 className="text-lg font-bold ml-2">Labs & Subscriptions</h1>
          <button onClick={() => navigate("/account/billing")} className="ml-auto text-sm text-[#0078d4] hover:underline" data-testid="billing-manage">Manage subscriptions</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {legacy && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300" data-testid="billing-legacy-note">You have complimentary access to all labs on your account.</div>}

          {owned.length > 0 && (
            <div>
              <h2 className="font-semibold mb-3">My labs</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {owned.map((l) => (
                  <div key={l.slug} className="rounded-xl border border-emerald-500/40 bg-card p-4" data-testid={`owned-${l.slug}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{l.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center gap-1"><Check className="h-3 w-3" /> Active</span>
                    </div>
                    <button onClick={() => navigate(l.route)} className="mt-3 w-full h-9 rounded-lg bg-[#0078d4] hover:bg-[#106ebe] text-white text-sm font-medium flex items-center justify-center gap-1.5" data-testid={`launch-${l.slug}`}>Launch <ArrowRight className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">{owned.length ? "Add a lab" : "Choose your labs"}</h2>
              <div className="flex rounded-lg border border-border overflow-hidden text-sm" data-testid="interval-toggle">
                <button onClick={() => setInterval("monthly")} className={`px-3 py-1.5 ${interval === "monthly" ? "bg-[#0078d4] text-white" : "bg-card"}`} data-testid="interval-monthly">Monthly</button>
                <button onClick={() => setInterval("annual")} className={`px-3 py-1.5 ${interval === "annual" ? "bg-[#0078d4] text-white" : "bg-card"}`} data-testid="interval-annual">Annual</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {available.map((l) => {
                const selected = cart.includes(l.slug);
                const annualUnavailable = interval === "annual" && !l.annual_enabled;
                const price = interval === "annual" ? l.annual_price_cents : l.monthly_price_cents;
                return (
                  <button key={l.slug} disabled={annualUnavailable} onClick={() => toggle(l.slug)} data-testid={`lab-card-${l.slug}`}
                    className={`text-left rounded-xl border p-4 transition ${selected ? "border-[#0078d4] ring-2 ring-[#0078d4]/30" : "border-border"} ${annualUnavailable ? "opacity-50 cursor-not-allowed" : "hover:border-[#0078d4]/60"} bg-card`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{l.name}</span>
                      <span className={`h-5 w-5 rounded-md grid place-items-center border ${selected ? "bg-[#0078d4] border-[#0078d4]" : "border-border"}`}>{selected && <Check className="h-3.5 w-3.5 text-white" />}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.description}</p>
                    <div className="mt-3 text-sm font-semibold">
                      {annualUnavailable ? <span className="text-muted-foreground text-xs">Annual coming soon</span>
                        : <>{money(price)}<span className="text-muted-foreground font-normal">/{interval === "annual" ? "yr" : "mo"}</span></>}
                    </div>
                  </button>
                );
              })}
            </div>
            {available.length === 0 && <p className="text-sm text-muted-foreground">You're subscribed to every available lab. 🎉</p>}
          </div>
        </div>

        {/* cart / review */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-border bg-card p-5 sticky top-6" data-testid="cart-summary">
            <div className="flex items-center gap-2 font-semibold mb-3"><ShoppingCart className="h-4 w-4" /> Your subscription</div>
            {cart.length === 0 && <p className="text-sm text-muted-foreground">Select one or more labs to continue.</p>}
            {review && (
              <div className="space-y-2">
                {review.items.map((it) => (
                  <div key={it.product} className="flex justify-between text-sm" data-testid={`review-item-${it.product}`}>
                    <span>{it.name} <span className="text-muted-foreground">· {it.interval}</span></span>
                    <span className="font-medium">{money(it.price_cents)}</span>
                  </div>
                ))}
                <div className="border-t border-border pt-2 flex justify-between font-semibold" data-testid="review-total">
                  <span>Total due now</span><span>{money(review.subtotal_cents)}</span>
                </div>
                <p className="text-xs text-muted-foreground">Then {money(review.recurring_cents)} per {interval === "annual" ? "year" : "month"}. Cancel anytime.</p>
                {manual ? (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2" data-testid="manual-fallback">
                    <p>{manual.note}</p>
                    <a href={manual.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[#0078d4] hover:underline">Open Square payment <ExternalLink className="h-3.5 w-3.5" /></a>
                  </div>
                ) : (
                  <button onClick={checkout} disabled={busy} data-testid="checkout-btn" className="mt-2 w-full h-11 rounded-lg bg-[#0078d4] hover:bg-[#106ebe] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure payment <ArrowRight className="h-4 w-4" /></>}
                  </button>
                )}
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 justify-center"><Lock className="h-3 w-3" /> Payments are processed securely by Stripe.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
